import React, { useEffect, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import {
    Box, Typography, Button, Table, TableHead, TableRow, TableBody, Paper, TableContainer,
    Chip, TextField, MenuItem, Select, FormControl, InputLabel, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, InputAdornment, Pagination, FormControlLabel, Switch, Tooltip, IconButton,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import {
    getVouchersRequest,
    createVoucherRequest, resetCreateVoucher,
    updateVoucherRequest, resetUpdateVoucher,
    deleteVoucherRequest, resetDeleteVoucher,
} from '../../../redux/modules/voucher/slice';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import { toast } from 'react-toastify';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-GB');
};

const emptyForm = {
    code: '', description: '', discountType: 'percentage', value: '',
    minOrderAmount: '', expiresAt: '', isActive: true,
};

const toDateInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
};

const Vouchers = () => {
    const dispatch = useDispatch();
    const { getVouchersRes, createVoucherRes, updateVoucherRes, deleteVoucherRes } = useSelector(state => state.voucher);
    const { currentBranch } = useSelector(state => state.branch);

    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [formDialog, setFormDialog] = useState({ open: false, mode: 'create' });
    const [form, setForm] = useState(emptyForm);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const limit = 20;

    const fetchVouchers = useCallback(() => {
        const params = { page, limit };
        if (searchQuery) params.search = searchQuery;
        dispatch(getVouchersRequest(params));
    }, [dispatch, page, limit, searchQuery]);

    useEffect(() => {
        dispatch(setMainHeading('Vouchers & Discounts'));
    }, [dispatch]);

    useEffect(() => {
        if (currentBranch?.id) fetchVouchers();
    }, [currentBranch?.id, fetchVouchers]);

    useEffect(() => {
        if (createVoucherRes?.success) {
            toast.success(createVoucherRes.message || 'Voucher created');
            dispatch(resetCreateVoucher());
            setFormDialog({ open: false, mode: 'create' });
            fetchVouchers();
        } else if (createVoucherRes && !createVoucherRes.success) {
            toast.error(createVoucherRes.message || 'Failed to create voucher');
            dispatch(resetCreateVoucher());
        }
    }, [createVoucherRes]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (updateVoucherRes?.success) {
            toast.success(updateVoucherRes.message || 'Voucher updated');
            dispatch(resetUpdateVoucher());
            setFormDialog({ open: false, mode: 'create' });
            fetchVouchers();
        } else if (updateVoucherRes && !updateVoucherRes.success) {
            toast.error(updateVoucherRes.message || 'Failed to update voucher');
            dispatch(resetUpdateVoucher());
        }
    }, [updateVoucherRes]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (deleteVoucherRes?.success) {
            toast.success(deleteVoucherRes.message || 'Voucher deleted');
            dispatch(resetDeleteVoucher());
            setDeleteDialog({ open: false, id: null });
            fetchVouchers();
        } else if (deleteVoucherRes && !deleteVoucherRes.success) {
            toast.error(deleteVoucherRes.message || 'Failed to delete voucher');
            dispatch(resetDeleteVoucher());
        }
    }, [deleteVoucherRes]); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreate = () => {
        setForm(emptyForm);
        setFormDialog({ open: true, mode: 'create' });
    };

    const openEdit = (voucher) => {
        setForm({
            _id: voucher._id,
            code: voucher.code || '',
            description: voucher.description || '',
            discountType: voucher.discountType || 'percentage',
            value: voucher.value ?? '',
            minOrderAmount: voucher.minOrderAmount ?? '',
            expiresAt: toDateInput(voucher.expiresAt),
            isActive: voucher.isActive !== false,
        });
        setFormDialog({ open: true, mode: 'edit' });
    };

    const handleSubmit = () => {
        if (!form.code?.trim()) { toast.error('Voucher code is required'); return; }
        if (form.value === '' || Number(form.value) < 0) { toast.error('Enter a valid discount value'); return; }
        if (form.discountType === 'percentage' && Number(form.value) > 100) {
            toast.error('Percentage discount cannot exceed 100'); return;
        }
        const payload = {
            code: form.code.trim().toUpperCase(),
            description: form.description,
            discountType: form.discountType,
            value: Number(form.value),
            minOrderAmount: form.minOrderAmount === '' ? 0 : Number(form.minOrderAmount),
            expiresAt: form.expiresAt || null,
            isActive: form.isActive,
        };
        if (formDialog.mode === 'edit') {
            dispatch(updateVoucherRequest({ _id: form._id, ...payload }));
        } else {
            dispatch(createVoucherRequest(payload));
        }
    };

    const vouchers = getVouchersRes?.data || [];
    const totalCount = getVouchersRes?.dataCount || 0;
    const totalPages = Math.ceil(totalCount / limit);

    const renderDiscount = (v) => (v.discountType === 'percentage' ? `${v.value}%` : `$${Number(v.value).toFixed(2)}`);

    const isExpired = (v) => v.expiresAt && new Date(v.expiresAt) < new Date();

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <TextField
                    size="small"
                    placeholder="Search by code…"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                    sx={{ minWidth: 280 }}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    New Voucher
                </Button>
            </Box>

            <Paper className='cardStyle'>
                <TableContainer sx={{ overflowX: { xs: 'auto', md: 'visible' } }}>
                    <Table className='commonTable'>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Code</StyledTableCell>
                                <StyledTableCell>Description</StyledTableCell>
                                <StyledTableCell align="right">Discount</StyledTableCell>
                                <StyledTableCell align="right">Min. Order</StyledTableCell>
                                <StyledTableCell>Expires</StyledTableCell>
                                <StyledTableCell align="right">Redeemed</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vouchers.length > 0 ? vouchers.map((v) => (
                                <StyledTableRow key={v._id}>
                                    <StyledTableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{v.code}</StyledTableCell>
                                    <StyledTableCell>{v.description || '—'}</StyledTableCell>
                                    <StyledTableCell align="right">{renderDiscount(v)}</StyledTableCell>
                                    <StyledTableCell align="right">{v.minOrderAmount ? `$${Number(v.minOrderAmount).toFixed(2)}` : '—'}</StyledTableCell>
                                    <StyledTableCell>{formatDate(v.expiresAt)}</StyledTableCell>
                                    <StyledTableCell align="right">{v.redemptionCount || 0}</StyledTableCell>
                                    <StyledTableCell>
                                        {isExpired(v) ? (
                                            <Chip label="Expired" color="default" size="small" />
                                        ) : v.isActive ? (
                                            <Chip label="Active" color="success" size="small" />
                                        ) : (
                                            <Chip label="Inactive" color="warning" size="small" />
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Tooltip title="Edit">
                                            <IconButton size="small" color="primary" onClick={() => openEdit(v)}>
                                                <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, id: v._id })}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8} align="center">
                                        <Typography color="text.secondary" sx={{ py: 3 }}>No vouchers found</Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <Pagination count={totalPages} page={page} onChange={(e, val) => setPage(val)} />
                    </Box>
                )}
            </Paper>

            {/* Create / Edit Dialog */}
            <Dialog open={formDialog.open} onClose={() => setFormDialog({ open: false, mode: 'create' })} maxWidth="sm" fullWidth>
                <DialogTitle>{formDialog.mode === 'edit' ? 'Edit Voucher' : 'New Voucher'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Code"
                            fullWidth
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                            inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
                            placeholder="e.g. NEWYEAR25"
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                        <Stack direction="row" spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={form.discountType}
                                    label="Type"
                                    onChange={(e) => setForm({ ...form, discountType: e.target.value })}
                                >
                                    <MenuItem value="percentage">Percentage</MenuItem>
                                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label={form.discountType === 'percentage' ? 'Percent' : 'Amount'}
                                type="number"
                                fullWidth
                                value={form.value}
                                onChange={(e) => setForm({ ...form, value: e.target.value })}
                                InputProps={form.discountType === 'percentage'
                                    ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                                    : { startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Minimum Order"
                                type="number"
                                fullWidth
                                value={form.minOrderAmount}
                                onChange={(e) => setForm({ ...form, minOrderAmount: e.target.value })}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            />
                            <TextField
                                label="Expires"
                                type="date"
                                fullWidth
                                value={form.expiresAt}
                                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Stack>
                        <FormControlLabel
                            control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
                            label="Active"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFormDialog({ open: false, mode: 'create' })}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {formDialog.mode === 'edit' ? 'Save Changes' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
                <DialogTitle>Delete Voucher</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this voucher? It will no longer be redeemable.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={() => dispatch(deleteVoucherRequest(deleteDialog.id))}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Vouchers;
